// Shared reflect-back helper for the RTE colour tools.
//
// A dynamic brand colour applied to a text selection records its merge-tag KEY
// (brace-less, e.g. "user.brandPrimaryColor") on the wrapper span in a
// `data-bay-<cssProp>-var` attribute, while the inline `style` keeps the resolved hex.
// On reopening the picker we read that key back so it can re-open in Dynamic mode.
//
// The wrapper can sit either ABOVE the selection's common ancestor (caret/inside the
// span) or BELOW it (the selection wraps the whole span, so the common ancestor is the
// contenteditable). So we look up the ancestor chain first, then fall back to a
// descendant search within the selection range.
export function findDynamicColorKey(
	range: Range,
	element: HTMLElement,
	attr: string,
): string | null {
	const ancestorMatch = element.closest<HTMLElement>(`[${attr}]`);
	if (ancestorMatch) {
		const key = ancestorMatch.getAttribute(attr);
		if (key) return key;
	}

	// Descendant case: the selection wraps the styled span, so the common ancestor is
	// an element above it. Scan the styled wrappers inside the container and return the
	// first one the selection actually intersects.
	const candidates = Array.from(element.querySelectorAll<HTMLElement>(`[${attr}]`));
	for (const candidate of candidates) {
		let intersects = true;
		try {
			intersects = range.intersectsNode(candidate);
		} catch {
			// intersectsNode can throw on detached/cross-root nodes; fall back to
			// containment so a single wrapper inside the selection is still matched.
			intersects = range.commonAncestorContainer.contains(candidate);
		}
		if (intersects) {
			const key = candidate.getAttribute(attr);
			if (key) return key;
		}
	}

	return null;
}
